import { AmountUsd } from '@ui/components/Amount/AmountUsd';
import { Percentage } from '@ui/components/Amount/Percentage';
import { ArrowDown, X } from '@ui/components/icons';
import { MassTokenSelectModal } from '@ui/features/MassiveToken/MassTokenSelectModal';
import { useIsPairClose } from '@ui/features/RWA/hooks/useIsPairClose';
import { SleepIndicator } from '@ui/features/RWA/SleepIndicator';
import { useDataChainId } from '@ui/hooks/datachain';
import { useTokenDisplayDecimals } from '@ui/hooks/useTokenDisplayDecimals';
import { Portal } from '@ui/lib/park/portal';
import { Collapsible } from '@ui/lib/park/ui/collapsible';
import { Drawer } from '@ui/lib/park/ui/drawer';
import { Icon } from '@ui/lib/park/ui/icon';
import { Text } from '@ui/lib/park/ui/text';
import { usePair24hAgoPrice } from '@ui/services/backend/hooks/oracle/usePair24hAgoPrice';
import { forwardRef, Ref, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Flex,
  FlexProps,
  HStack,
  HstackProps,
  styled,
} from 'styled-system/jsx';

import { useIsTradePageContainerDesktop } from '../../hooks/useIsTradePageContainerDesktop';
import { useMarketPrice } from '../../hooks/useMarketPrice';
import { useTradeFlowPriceChartStore } from '../../store/useTradeFlowPriceChartStore';
import { useTradingPair } from '../../store/useTradingPair';
import { simpleChartProps } from '../PriceChart/constants';
import { PriceChart } from '../PriceChart/PriceChart';
import { createDatafeed } from '../PriceChart/utils';
import { useTradeModuleContext } from '../TradeModuleConfirm/hooks/useTradeModuleContext';
import { TradeButton } from './TradeButton/TradeButton';
import { TradeModule } from './TradeModule';

export const TradeModuleModal = (p: FlexProps) => {
  const { isTradeModuleModalOpen, setIsTradeModuleModalOpen } =
    useTradeModuleContext();
  const { isConfirmTradeIntroOpen } = useTradeModuleContext();
  const { value: isContainerDesktop, isPending } =
    useIsTradePageContainerDesktop();

  // Avoid "tap jitter" on mobile: a normal tap usually has a few pixels of movement.
  // Only start the drag-to-close gesture after the user has pulled down enough.
  const DRAG_START_THRESHOLD_PX = 8;

  const isDragging = useRef(false);
  const isTouching = useRef(false);
  const isClosing = useRef(false);
  const startY = useRef(0);
  const bodyRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const translateY = useRef(0);

  useEffect(() => {
    if (isTradeModuleModalOpen) {
      translateY.current = 0;
      isDragging.current = false;
      isClosing.current = false;
      if (contentRef.current) {
        contentRef.current.style.transform = '';
        contentRef.current.style.transition = '';
        contentRef.current.style.animation = '';
      }
    }
  }, [isTradeModuleModalOpen]);

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (isClosing.current) return;
    if (e.touches.length !== 1) return;
    // If user is interacting with an input/button/link inside the drawer, don't hijack the gesture.
    const target = e.target as HTMLElement | null;
    if (
      target?.closest?.(
        'input, textarea, select, button, a, [role="button"], [contenteditable="true"]',
      )
    ) {
      return;
    }
    // Only allow drag-to-close when the scrollable body is already at the top.
    if (bodyRef.current && bodyRef.current.scrollTop > 0) return;
    isTouching.current = true;
    isDragging.current = false;
    startY.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (isClosing.current) return;
    if (!isTouching.current) return;
    // If user started scrolling the drawer content, never treat it as a close-gesture.
    if (bodyRef.current && bodyRef.current.scrollTop > 0) return;
    const currentY = e.touches[0].clientY;
    const delta = currentY - startY.current;
    if (delta <= 0) return;

    // Only start dragging after a small threshold to prevent "tap jitter".
    if (!isDragging.current) {
      if (delta < DRAG_START_THRESHOLD_PX) return;
      isDragging.current = true;
      if (contentRef.current) {
        // Mark dragging so we can hard-disable recipe animations/transitions via CSS.
        contentRef.current.dataset.dragging = 'true';
        contentRef.current.style.transition = 'none';
        // disable recipe animation while user is dragging so we fully control transform
        contentRef.current.style.animation = 'none';
      }
    }

    // When actively dragging, prevent native scroll/rubber-band from fighting our transform.
    e.preventDefault();
    if (contentRef.current) {
      translateY.current = delta;
      contentRef.current.style.transform = `translateY(${delta}px)`;
    }
  };

  const handleTouchEnd = () => {
    if (isClosing.current) return;
    isTouching.current = false;
    // If user only tapped / didn't exceed threshold, do nothing (avoids any visible jitter).
    if (!isDragging.current) return;
    isDragging.current = false;
    if (translateY.current > 150) {
      const el = contentRef.current;
      if (!el) {
        setIsTradeModuleModalOpen?.(false);
        
return;
      }

      // Smooth close from current drag position, without snapping back to fully-open.
      // We keep the drawer "open" while we animate the content down, then flip open=false.
      // Important: remove dragging flag so our CSS `transition: none !important` doesn't kill
      // the close animation.
      delete el.dataset.dragging;
      isClosing.current = true;
      el.style.animation = 'none';
      // Ensure a real transition happens from the current dragged position.
      el.style.transition = 'none';
      el.style.transform = `translateY(${translateY.current}px)`;
      window.requestAnimationFrame(() => {
        el.style.transition = 'transform 0.25s ease-out';
        el.style.transform = 'translateY(100%)';
      });

      const finish = () => {
        // keep inline transform in place during close to avoid Ark UI closed-state animation
        // snapping back to translateY(0) before exiting.
        isClosing.current = false;
        translateY.current = 0;
        setIsTradeModuleModalOpen?.(false);
      };

      const onTransitionEnd = (evt: TransitionEvent) => {
        if (evt.propertyName !== 'transform') return;
        el.removeEventListener('transitionend', onTransitionEnd);
        finish();
      };

      el.addEventListener('transitionend', onTransitionEnd);
      // Fallback in case transitionend doesn't fire (rare on mobile / interrupted).
      window.setTimeout(() => {
        el.removeEventListener('transitionend', onTransitionEnd);
        if (isClosing.current) finish();
      }, 350);
    } else {
      translateY.current = 0;
      if (contentRef.current) {
        // User preference: snap back to fully-open without any extra "slide" / re-opening animation.
        // Also keep animation disabled here to avoid retriggering Ark UI open keyframes.
        contentRef.current.style.transition = 'none';
        contentRef.current.style.transform = 'translateY(0px)';
        contentRef.current.style.animation = 'none';
        delete contentRef.current.dataset.dragging;
      }
    }
  };

  const handleTouchCancel = () => {
    isTouching.current = false;
    if (!isDragging.current) return;
    isDragging.current = false;
    translateY.current = 0;
    if (contentRef.current) {
      contentRef.current.style.transition = 'none';
      contentRef.current.style.transform = 'translateY(0px)';
      contentRef.current.style.animation = 'none';
      delete contentRef.current.dataset.dragging;
    }
  };

  useEffect(() => {
    // close modal on trade section desktop size
    if (isPending || isContainerDesktop) {
      setIsTradeModuleModalOpen?.(false);
    }
  }, [isContainerDesktop, isPending, setIsTradeModuleModalOpen]);

  if (isContainerDesktop) {
    return null;
  }

  return (
    <Flex {...p}>
      <TradeButton
        display={{
          base: 'flex',
          desktop: 'none',
        }}
        p="12px 16px"
        bg="v2.bg.panel"
        w="full"
        setIsTradeModuleOpen={(isOpen) => setIsTradeModuleModalOpen?.(isOpen)}
      />
      <Drawer.Root
        open={isTradeModuleModalOpen}
        onOpenChange={(e) => setIsTradeModuleModalOpen?.(e.open)}
        modal={true} // allow scrolling in mobile
        closeOnInteractOutside={!isConfirmTradeIntroOpen} // prevent intro clicks from closing trade drawer
        lazyMount
        unmountOnExit
        direction="bottom"
        // size="md"
      >
        <Portal>
          <Drawer.Backdrop
            backdropFilter="none"
            bg="rgba(0, 0, 0, 0.4)"
            style={{
              visibility: isConfirmTradeIntroOpen ? 'hidden' : 'visible',
            }}
          />
          <Drawer.Positioner
            w={{
              // one time usage , using inline instead of recipes
              base: 'full',
              tablet: '425px',
            }}
            style={{
              visibility: isConfirmTradeIntroOpen ? 'hidden' : 'visible',
            }}
          >
            <Drawer.Content
              ref={contentRef}
              display="flex"
              flexDir="column"
              h="66dvh"
              bg="v2.bg.panel"
              borderTopRadius="8px"
              boxShadow="sheet"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onTouchCancel={handleTouchCancel}
              css={{
                // The drawer recipe applies open/close keyframe animations (e.g. drawer-in-bottom).
                // While user is dragging, we must hard-disable them or Safari can still animate
                // the "snap back" even if we set inline styles.
                '&[data-dragging="true"]': {
                  animation: 'none !important',
                  transition: 'none !important',
                },
              }}
              style={{
                pointerEvents: isConfirmTradeIntroOpen ? 'none' : 'auto',
              }}
            >
              {/* <Drawer.Title p="16px" asChild>
                <Head />
              </Drawer.Title> */}

              <Drawer.Body
                p={0}
                flex={1}
                ref={bodyRef}
                css={{
                  '&::-webkit-scrollbar': {
                    display: 'none',
                  },
                  scrollbarWidth: 'none',
                  overscrollBehavior: 'contain',
                }}
              >
                {/* <TradeFlowPriceChart /> */}
                <TradeModule.Root
                  flex={1}
                  display="flex"
                  flexDir="column"
                  justifyContent="space-between"
                >
                  <Box>
                    <Box
                      borderTop="1px solid"
                      borderBottom="1px solid"
                      borderColor="v2.border.default"
                      position="sticky"
                      top={0}
                      zIndex={10}
                      bg="v2.bg.panel"
                    >
                      <TradeModule.Head />
                    </Box>
                    <Box p="6px 16px 0">
                      <TradeModule.Inputs />
                      <TradeModule.FooterMobile mt="2" />
                    </Box>
                  </Box>
                  <Box
                    borderTop="1px solid"
                    borderColor="v2.border.default"
                    position="sticky"
                    bottom={0}
                    zIndex={10}
                    bg="v2.bg.panel"
                    p="12px 16px"
                  >
                    <TradeModule.FooterConfirmButton />
                  </Box>
                </TradeModule.Root>
              </Drawer.Body>
            </Drawer.Content>
          </Drawer.Positioner>
        </Portal>
      </Drawer.Root>
    </Flex>
  );
};

const Head = forwardRef((p: HstackProps, ref: Ref<HTMLDivElement>) => {
  const tradingPair = useTradingPair((s) => s.useTradingPair());
  const marketPrice = useMarketPrice();
  const change24h = usePair24hAgoPrice({
    pairBase: tradingPair.address,
    pythId: tradingPair.pythPriceFeedId,
    currentPrice: marketPrice ?? undefined,
  });

  const priceDisplayDecimals = useTokenDisplayDecimals(
    tradingPair.baseSymbol,
    tradingPair.priceDisplayDecimals,
  );
  const isPairSleeping = useIsPairClose(tradingPair);

  return (
    <HStack justifyContent="space-between" ref={ref} {...p}>
      <MassTokenSelectModal>
        <HStack gap={0}>
          <styled.img
            src={tradingPair.image}
            alt="token icon"
            boxSize="20px"
            borderRadius="6px"
          />
          <Text ml="8px" textStyle="headings.large">
            {tradingPair.baseName}
          </Text>
          {isPairSleeping && <SleepIndicator ml="8px" />}
          <AmountUsd
            ml="12px"
            textStyle="text.large"
            value={marketPrice}
            opts={{
              displayDecimals: priceDisplayDecimals,
            }}
          />
          <Percentage
            ml="4px"
            textStyle="text.small"
            value={change24h?.rate}
            opts={{
              isShowSign: true,
            }}
          />
          <Icon boxSize="16px">
            <ArrowDown />
          </Icon>
        </HStack>
      </MassTokenSelectModal>
      <Drawer.CloseTrigger>
        <Icon w="24px" h="24px">
          <X />
        </Icon>
      </Drawer.CloseTrigger>
    </HStack>
  );
});

Head.displayName = 'Head';

const chartRatio = 375 / 160;

const TradeFlowPriceChart = () => {
  const tradingPair = useTradingPair((s) => s.useTradingPair());
  const chainId = useDataChainId();
  // use zustand store since the trigger is too independent
  const isTradeFlowPriceChartOpen = useTradeFlowPriceChartStore(
    (s) => s.isOpen,
  );
  const simpleChartDataFeed = useMemo(
    () =>
      createDatafeed({
        chartId: 'trade-flow-simple-chart',
        chainId,
      }),
    [chainId],
  );

  return (
    <Collapsible.Root open={isTradeFlowPriceChartOpen}>
      <Collapsible.Content>
        <PriceChart
          id="trade-flow-simple-chart"
          tradingPair={tradingPair}
          datafeed={simpleChartDataFeed}
          chartProps={simpleChartProps}
          w="full"
          aspectRatio={chartRatio}
        />
      </Collapsible.Content>
    </Collapsible.Root>
  );
};
